import React, { useEffect, useState } from 'react';
import {
    Radar,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    ResponsiveContainer,
    Tooltip
} from 'recharts';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { swotAnalysisService, SwotData } from '../services/SwotAnalysisService';

interface FormattedSwotData {
    subject: string;
    value: number;
    fullMark: number;
}

const SwotRadarChart: React.FC = () => {
    const [swotData, setSwotData] = useState<SwotData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [maxDomainValue, setMaxDomainValue] = useState<number>(300);

    useEffect(() => {
        const fetchSwotData = async () => {
            try {
                setLoading(true);
                const data = await swotAnalysisService.obterDadosSwot();
                setSwotData(data);

                // Calculate the maximum domain value based on the highest score
                if (data) {
                    const maxScore = Math.max(
                        data.pontosFortesPontuacaoTotal,
                        data.ameacasPontuacaoTotal,
                        data.pontosFracosPontuacaoTotal,
                        data.oportunidadesPontuacaoTotal
                    );

                    // Add 20% buffer to the max value for better visualization
                    const domainMax = Math.ceil(maxScore * 1.2);
                    setMaxDomainValue(domainMax);
                }

                setLoading(false);
            } catch (err) {
                console.error('Erro ao carregar dados SWOT:', err);
                setError('Não foi possível carregar os dados da análise SWOT');
                setLoading(false);
            }
        };

        fetchSwotData();
    }, []);

    // Formatar dados para o gráfico de radar
    const formatDataForRadarChart = (data: SwotData | null): FormattedSwotData[] => {
        if (!data) return [];

        return [
            { subject: 'Pontos Fortes', value: data.pontosFortesPontuacaoTotal, fullMark: maxDomainValue },
            { subject: 'Ameaças', value: data.ameacasPontuacaoTotal, fullMark: maxDomainValue },
            { subject: 'Pontos Fracos', value: data.pontosFracosPontuacaoTotal, fullMark: maxDomainValue },
            { subject: 'Oportunidades', value: data.oportunidadesPontuacaoTotal, fullMark: maxDomainValue }
        ];
    };

    if (loading) {
        return <Typography>Carregando análise SWOT...</Typography>;
    }

    if (error) {
        return <Typography color="error">{error}</Typography>;
    }

    const radarData = formatDataForRadarChart(swotData);

    return (
        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            height: '100%'
        }}>
            {/* Gráfico de Radar compacto */}
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 800,
                    height: 750,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart
                        outerRadius={80}
                        data={radarData}
                        margin={{ top: 8, right: 12, bottom: 8, left: 12 }}
                    >
                        <PolarGrid />
                        <PolarAngleAxis
                            dataKey="subject"
                            tick={{
                                fontSize: 14,
                                fontWeight: 'bold',
                                fill: '#666666',
                                dy: 4
                            }}
                            tickLine={false}
                        />
                        <PolarRadiusAxis
                            angle={90}
                            domain={[0, maxDomainValue]}
                            tick={false}
                            tickCount={5}
                            axisLine={false}
                        />
                        <Radar
                            name="Pontuação"
                            dataKey="value"
                            stroke="#2463E9"
                            fill="#2463E9"
                            fillOpacity={0.6}
                        />
                        <Tooltip 
                            formatter={(value) => [`${value}`, 'Pontuação']}
                            contentStyle={{
                                fontSize: '14px',
                                padding: '4px',
                                borderRadius: '4px'
                            }}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default SwotRadarChart;
